import React from 'react';
import { getResponsiveMultiplier } from '../../utils/visualizerUtils';

// Particle system for mirror spectrum effects
let particles = [];

const createParticle = (x, y, intensity) => {
  return {
    x: x,
    y: y,
    vx: (Math.random() - 0.5) * 4,
    vy: (Math.random() - 0.5) * 4 - 2,
    life: 1.0,
    decay: 0.02 + Math.random() * 0.02,
    size: 2 + Math.random() * 3,
    hue: 120 + Math.random() * 60, // Green-cyan range
    intensity: intensity / 255
  };
};

const updateParticles = () => {
  for (let i = particles.length - 1; i >= 0; i--) {
    const particle = particles[i];
    
    particle.x += particle.vx;
    particle.y += particle.vy;
    particle.life -= particle.decay;
    particle.vy += 0.1; // Gravity effect
    
    if (particle.life <= 0) {
      particles.splice(i, 1);
    }
  }
};

const drawParticles = (ctx) => {
  particles.forEach(particle => {
    const alpha = particle.life * particle.intensity;
    
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = `hsla(${particle.hue}, 80%, 70%, ${alpha})`;
    ctx.beginPath();
    ctx.arc(particle.x, particle.y, particle.size * particle.life, 0, Math.PI * 2);
    ctx.fill();
    
    // Add glow effect
    ctx.shadowBlur = 10;
    ctx.shadowColor = `hsla(${particle.hue}, 80%, 70%, ${alpha * 0.5})`;
    ctx.fill();
    ctx.restore();
  });
};

export const drawMirrorSpectrum = (ctx, dataArray, width, height, time) => {
  if (!ctx || !dataArray || width <= 0 || height <= 0) return;

  try {
    // Clear canvas with fade effect for trailing
    ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.fillRect(0, 0, width, height);

    // Create gradient background - green theme
    const gradient = ctx.createRadialGradient(width/2, height/2, 0, width/2, height/2, width/2);
    gradient.addColorStop(0, 'rgba(34, 197, 94, 0.1)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0.8)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Draw frequency bars using optimized approach (reduced bar count for performance)
    const bufferLength = dataArray.length;
    const maxBars = Math.min(192, bufferLength); // Increased from 128 to 192 (25% reduction)
    const step = Math.floor(bufferLength / maxBars); // Skip some frequency bins
    const barWidth = width / maxBars * getResponsiveMultiplier(); // Use responsive multiplier
    let x = 0;

    for (let i = 0; i < maxBars; i++) {
      const dataIndex = Math.min(i * step, bufferLength - 1);
      const barHeight = (dataArray[dataIndex] / 255) * height * 0.4; // Half height for mirroring

      // Create bar gradient - green-cyan range
      const barGradient = ctx.createLinearGradient(0, height/2 - barHeight, 0, height/2 + barHeight);
      const hue = (i / maxBars) * 120 + 120; // Green-cyan range
      barGradient.addColorStop(0, `hsla(${hue}, 80%, 60%, 1)`);
      barGradient.addColorStop(0.5, `hsla(${hue + 20}, 90%, 70%, 0.8)`);
      barGradient.addColorStop(1, `hsla(${hue}, 80%, 60%, 1)`);

      ctx.fillStyle = barGradient;
      
      // Top bar (above center line)
      ctx.fillRect(x, height/2 - barHeight, barWidth, barHeight);
      
      // Bottom bar (mirror - below center line)
      ctx.fillRect(x, height/2, barWidth, barHeight);

      // Create particles for intense frequencies
      if (dataArray[dataIndex] > 150 && Math.random() > 0.8) {
        // Particles above center line
        particles.push(createParticle(x + barWidth/2, height/2 - barHeight, dataArray[dataIndex]));
        // Particles below center line (mirror)
        particles.push(createParticle(x + barWidth/2, height/2 + barHeight, dataArray[dataIndex]));
      }

      x += barWidth + 1; // Small gap between bars
    }

    // Draw central line
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, height/2);
    ctx.lineTo(width, height/2);
    ctx.stroke();

    // Add glow to central line
    ctx.save();
    ctx.shadowBlur = 10;
    ctx.shadowColor = 'rgba(255, 255, 255, 0.3)';
    ctx.stroke();
    ctx.restore();

    // Update and draw particles
    updateParticles();
    drawParticles(ctx);

    // Add mirror spectrum effects
    drawMirrorEffects(ctx, dataArray, width, height, time);

  } catch (error) {
    console.error('❌ Mirror Spectrum visualization error:', error);
  }
};

const drawMirrorEffects = (ctx, dataArray, width, height, time) => {
  // Calculate average intensity for effects
  const avgIntensity = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;
  const normalizedIntensity = avgIntensity / 255;

  // Draw reflection shimmer effects
  drawReflectionShimmer(ctx, width, height, time, normalizedIntensity);

  // Draw energy waves along the center line
  drawCenterLineWaves(ctx, width, height, time, normalizedIntensity);

  // Draw mirror distortion effects
  drawMirrorDistortion(ctx, dataArray, width, height, time);
};

const drawReflectionShimmer = (ctx, width, height, time, intensity) => {
  ctx.save();
  
  // Create shimmer effect across the mirror line
  const shimmerCount = 8;
  for (let i = 0; i < shimmerCount; i++) {
    const shimmerX = (time * 100 + i * 50) % (width + 100) - 50;
    const shimmerWidth = 20 + intensity * 30;
    const shimmerAlpha = intensity * 0.6;
    
    if (shimmerX >= -shimmerWidth && shimmerX <= width) {
      const gradient = ctx.createLinearGradient(
        shimmerX - shimmerWidth/2, height/2 - 20,
        shimmerX + shimmerWidth/2, height/2 + 20
      );
      gradient.addColorStop(0, `hsla(180, 80%, 80%, 0)`);
      gradient.addColorStop(0.5, `hsla(180, 90%, 90%, ${shimmerAlpha})`);
      gradient.addColorStop(1, `hsla(180, 80%, 80%, 0)`);
      
      ctx.fillStyle = gradient;
      ctx.fillRect(shimmerX - shimmerWidth/2, height/2 - 20, shimmerWidth, 40);
    }
  }
  
  ctx.restore();
};

const drawCenterLineWaves = (ctx, width, height, time, intensity) => {
  ctx.save();
  
  // Draw flowing waves along the center line
  const waveCount = 3;
  for (let w = 0; w < waveCount; w++) {
    const waveOffset = (w / waveCount) * Math.PI * 2;
    const waveSpeed = 1 + w * 0.3;
    const waveAmplitude = 15 + intensity * 25;
    const waveFrequency = 0.02 + w * 0.01;
    
    ctx.strokeStyle = `hsla(${150 + w * 30}, 80%, 70%, ${0.4 + intensity * 0.4})`;
    ctx.lineWidth = 2 + intensity * 2;
    
    ctx.beginPath();
    for (let x = 0; x <= width; x += 2) {
      const y = height/2 + 
                Math.sin(x * waveFrequency + time * waveSpeed + waveOffset) * waveAmplitude;
      
      if (x === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();
    
    // Add glow effect
    ctx.save();
    ctx.shadowBlur = 15;
    ctx.shadowColor = `hsla(${150 + w * 30}, 80%, 70%, ${0.3 + intensity * 0.3})`;
    ctx.stroke();
    ctx.restore();
  }
  
  ctx.restore();
};

const drawMirrorDistortion = (ctx, dataArray, width, height, time) => {
  ctx.save();
  
  // Create subtle distortion effects near the mirror line
  const distortionPoints = 20;
  for (let i = 0; i < distortionPoints; i++) {
    const dataIndex = Math.floor((i / distortionPoints) * dataArray.length);
    const intensity = dataArray[dataIndex] / 255;
    
    if (intensity > 0.5) {
      const x = (i / distortionPoints) * width;
      const distortionHeight = intensity * 30;
      const hue = 120 + (i / distortionPoints) * 60;
      
      // Distortion ripples above center line
      ctx.strokeStyle = `hsla(${hue}, 70%, 60%, ${intensity * 0.5})`;
      ctx.lineWidth = 1;
      
      for (let r = 0; r < 3; r++) {
        const rippleRadius = r * 10 + (time * 50) % 30;
        const rippleAlpha = intensity * (1 - r * 0.3) * (1 - (time * 50 % 30) / 30);
        
        if (rippleAlpha > 0.1) {
          ctx.save();
          ctx.globalAlpha = rippleAlpha;
          ctx.beginPath();
          ctx.arc(x, height/2 - distortionHeight, rippleRadius, 0, Math.PI * 2);
          ctx.stroke();
          
          // Mirror ripple below center line
          ctx.beginPath();
          ctx.arc(x, height/2 + distortionHeight, rippleRadius, 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
        }
      }
    }
  }
  
  ctx.restore();
};

// Export the component function that matches the expected interface
export function MirrorSpectrum({ ctx, dataArray, width, height, isPlaying }) {
  const time = Date.now() * 0.001; // Convert to seconds for smooth animation
  drawMirrorSpectrum(ctx, dataArray, width, height, time);
}